import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { X, Plus, Star, Heart, Lightbulb, Headphones, Music } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface AnnotationFormData {
  authenticityAssessment: 'real' | 'ai-generated' | 'uncertain';
  familiarityLevel: 'know' | 'familiar' | 'never' | 'uncertain';
  aestheticQuality: number; // 1-10
  playlistLikelihood: number; // 1-10
  musicalCreativity: number; // 1-10
  productionQuality: number; // 1-10
  emotionalEngagement: number; // 1-10
  aiAspects?: string[]; // Only if authenticity = 'ai-generated'
  moodTags: string[];
  aestheticComment?: string;
  songDescription?: string;
}

interface AnnotationFormProps {
  onSubmit: (data: AnnotationFormData, duration: number, listeningTime: number) => void;
  onError?: (error: string) => void;
  isLoading?: boolean;
  listeningTime?: number;
  resetTrigger?: number; // Used to reset form when song changes
}

const AI_ASPECTS_OPTIONS = [
  'Singing voice',
  'Instrument sounds',
  'Rhythm',
  'Harmony',
  'Lyrics',
  'Overall composition',
  'Everything',
  'Other'
];

const GEMS_MOOD_TAGS = [
  'Wonder',
  'Transcendence',
  'Tenderness',
  'Nostalgia',
  'Peacefulness',
  'Power',
  'Joyful activation',
  'Tension',
  'Sadness'
];

const RATING_LABELS = {
  aestheticQuality: { label: 'Aesthetic Quality', description: 'How aesthetically pleasing is this song?', icon: Star },
  playlistLikelihood: { label: 'Playlist Likelihood', description: 'How likely would you be to add this to your playlist?', icon: Heart },
  musicalCreativity: { label: 'Musical Creativity', description: 'How creative and original does this music sound?', icon: Lightbulb },
  productionQuality: { label: 'Production Quality', description: 'How well-produced does this song sound?', icon: Headphones },
  emotionalEngagement: { label: 'Emotional Engagement', description: 'How emotionally engaging is this song?', icon: Music },
};

function RatingScale({
  value,
  onChange,
  label,
  description,
  icon: Icon,
  disabled = false
}: {
  value: number | undefined;
  onChange: (value: number) => void;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  disabled?: boolean;
}) {
  const sliderValue = [value || 5]; // Use value or default to 5
  const handleSliderChange = (values: number[]) => {
    // Round to one decimal place
    const roundedValue = Math.round((values[0] || 5) * 10) / 10;
    onChange(roundedValue);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary" />
        <Label className="text-sm font-medium">{label}</Label>
        {value && (
          <span className="text-sm font-semibold text-primary">{value.toFixed(1)}/10</span>
        )}
      </div>
      <p className="text-xs text-muted-foreground">{description}</p>

      <div className="space-y-3">
        {/* Slider */}
        <div className="px-2">
          <Slider
            value={sliderValue}
            onValueChange={handleSliderChange}
            min={1}
            max={10}
            step={0.1}
            disabled={disabled}
            className="w-full"
          />
        </div>

        {/* Labels */}
        <div className="flex justify-between items-center px-1">
          <span className="text-xs text-muted-foreground">Poor (1.0)</span>
          <span className="text-xs text-muted-foreground">Excellent (10.0)</span>
        </div>
      </div>
    </div>
  );
}

export function AnnotationForm({ onSubmit, onError, isLoading = false, listeningTime = 0, resetTrigger }: AnnotationFormProps) {
  const [startTime, setStartTime] = useState(Date.now());
  const [moodTags, setMoodTags] = useState<string[]>([]);
  const [newMoodTag, setNewMoodTag] = useState('');
  const [selectedAiAspects, setSelectedAiAspects] = useState<string[]>([]);
  const [otherAiAspect, setOtherAiAspect] = useState('');

  const form = useForm<AnnotationFormData>({
    defaultValues: {
      // No preselections to avoid bias, but provide neutral defaults to prevent controlled/uncontrolled warnings
      authenticityAssessment: undefined,
      familiarityLevel: undefined,
      aestheticQuality: 5.0, // Neutral middle value
      playlistLikelihood: 5.0, // Neutral middle value
      musicalCreativity: 5.0, // Neutral middle value
      productionQuality: 5.0, // Neutral middle value
      emotionalEngagement: 5.0, // Neutral middle value
      moodTags: [],
      aestheticComment: '',
      songDescription: '',
    },
  });

  // Reset form when resetTrigger changes (new song)
  useEffect(() => {
    if (resetTrigger !== undefined) {
      form.reset({
        authenticityAssessment: undefined,
        familiarityLevel: undefined,
        aestheticQuality: 5.0,
        playlistLikelihood: 5.0,
        musicalCreativity: 5.0,
        productionQuality: 5.0,
        emotionalEngagement: 5.0,
        moodTags: [],
        aestheticComment: '',
        songDescription: '',
      });
      // Clear form validation errors
      form.clearErrors();
      setMoodTags([]);
      setNewMoodTag('');
      setSelectedAiAspects([]);
      setOtherAiAspect('');
      setStartTime(Date.now());
    }
  }, [resetTrigger, form]);

  const authenticityValue = form.watch('authenticityAssessment');

  const handleSubmit = (data: AnnotationFormData) => {
    try {
      // Calculate annotation duration
      const duration = Date.now() - startTime;

      // Validate required fields
      if (!data.authenticityAssessment) {
        throw new Error('Authenticity assessment is required');
      }

      if (!data.familiarityLevel) {
        throw new Error('Familiarity level is required');
      }

      // Validate all rating scales are filled
      const requiredRatings = ['aestheticQuality', 'playlistLikelihood', 'musicalCreativity', 'productionQuality', 'emotionalEngagement'];
      for (const rating of requiredRatings) {
        const value = data[rating as keyof AnnotationFormData];
        if (value === undefined || value === null || typeof value !== 'number') {
          throw new Error(`${rating.replace(/([A-Z])/g, ' $1').toLowerCase()} rating is required`);
        }
      }

      // Include mood tags and AI aspects
      const finalAiAspects = [...selectedAiAspects];
      if (otherAiAspect.trim()) {
        finalAiAspects.push(otherAiAspect.trim());
      }

      const finalData: AnnotationFormData = {
        ...data,
        moodTags,
        aiAspects: data.authenticityAssessment === 'ai-generated' ? finalAiAspects : undefined,
      };

      onSubmit(finalData, duration, listeningTime);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Form validation failed';
      onError?.(errorMessage);
    }
  };

  const addMoodTag = () => {
    const tag = newMoodTag.trim();
    if (tag && !moodTags.includes(tag)) {
      setMoodTags([...moodTags, tag]);
      setNewMoodTag('');
    }
  };

  const removeMoodTag = (tagToRemove: string) => {
    setMoodTags(moodTags.filter(tag => tag !== tagToRemove));
  };

  const toggleAiAspect = (aspect: string) => {
    setSelectedAiAspects(prev =>
      prev.includes(aspect)
        ? prev.filter(a => a !== aspect)
        : [...prev, aspect]
    );
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      addMoodTag();
    }
  };

  return (
    <Card id="song-annotation-card" className="w-full mx-auto">
      <CardHeader className="pb-4">
        <CardTitle className="text-xl md:text-2xl">Song Annotation</CardTitle>
        <p className="text-sm text-muted-foreground">
          Please provide your assessment of the music track you just heard.
        </p>
      </CardHeader>
      <CardContent className="px-4 md:px-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit, () => {
            // Scroll to the first field with a validation error
            setTimeout(() => {
              const firstError = document.querySelector('[data-slot="form-message"]');
              if (firstError) {
                firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 50);
          })} className="space-y-8">

            {/* Core Assessment */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Core Assessment</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="authenticityAssessment"
                  rules={{ required: "Authenticity assessment is required" }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Do you think this song is real or AI-generated? *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || ""}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select your assessment" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="real">Real (human-made)</SelectItem>
                          <SelectItem value="ai-generated">AI-generated</SelectItem>
                          <SelectItem value="uncertain">Uncertain</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="familiarityLevel"
                  rules={{ required: "Familiarity level is required" }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>How familiar are you with this song? *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || ""}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select familiarity level" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="know">Yes, I know this song</SelectItem>
                          <SelectItem value="familiar">Sounds familiar</SelectItem>
                          <SelectItem value="never">Never heard it</SelectItem>
                          <SelectItem value="uncertain">Uncertain</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* AI Aspects - Conditional */}
              {(authenticityValue === 'ai-generated' || authenticityValue === 'uncertain') && (
                <FormItem>
                  <FormLabel>Which aspects sound AI-generated to you?</FormLabel>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
                    {AI_ASPECTS_OPTIONS.filter(aspect => aspect !== 'Other').map((aspect) => (
                      <label
                        key={aspect}
                        className={cn(
                          "flex items-center space-x-2 p-3 rounded-md border cursor-pointer transition-colors text-sm",
                          selectedAiAspects.includes(aspect)
                            ? "bg-primary text-primary-foreground border-primary"
                            : "hover:bg-muted border-input"
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={selectedAiAspects.includes(aspect)}
                          onChange={() => toggleAiAspect(aspect)}
                          className="sr-only"
                        />
                        <span className="flex-1">{aspect}</span>
                      </label>
                    ))}
                  </div>
                  <div className="mt-3">
                    <Input
                      placeholder="Other AI aspects (describe)"
                      value={otherAiAspect}
                      onChange={(e) => setOtherAiAspect(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <FormDescription>
                    Select all aspects that sound artificially generated to you.
                  </FormDescription>
                </FormItem>
              )}
            </div>

            {/* Rating Scales */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Rating Scales</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 min-[1400px]:grid-cols-5 gap-6 lg:gap-8">
                {Object.entries(RATING_LABELS).map(([key, config]) => (
                  <FormField
                    key={key}
                    control={form.control}
                    name={key as keyof AnnotationFormData}
                    rules={{ required: `${config.label} rating is required` }}
                    render={({ field }) => (
                      <FormItem className="space-y-3">
                        <RatingScale
                          value={field.value as number | undefined}
                          onChange={field.onChange}
                          label={config.label}
                          description={config.description}
                          icon={config.icon}
                          disabled={isLoading}
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ))}
              </div>
            </div>

            {/* Mood Tags */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Mood and Atmosphere</h3>

              <FormItem>
                <FormLabel>Mood Tags</FormLabel>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a mood word (e.g., energetic, melancholic)"
                    value={newMoodTag}
                    onChange={(e) => setNewMoodTag(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={addMoodTag}
                    variant="outline"
                    size="sm"
                    disabled={!newMoodTag.trim()}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                {moodTags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {moodTags.map((tag, index) => (
                      <Badge key={index} variant="secondary" className="gap-1">
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeMoodTag(tag)}
                          className="ml-1 hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}

                {/* GEMS Mood Tag Suggestions */}
                <div className="mt-4">
                  <p className="text-sm text-muted-foreground mb-2">Suggested (GEMS emotional categories):</p>
                  <div className="flex flex-wrap gap-2">
                    {GEMS_MOOD_TAGS.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => {
                          if (!moodTags.includes(tag)) {
                            setMoodTags([...moodTags, tag]);
                          }
                        }}
                        disabled={moodTags.includes(tag)}
                        className={cn(
                          "px-3 py-1 text-xs rounded-full border transition-colors",
                          moodTags.includes(tag)
                            ? "bg-muted text-muted-foreground border-muted cursor-not-allowed"
                            : "bg-background text-foreground border-input hover:bg-muted hover:border-primary/50"
                        )}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>

                <FormDescription>
                  Add words that describe the mood or atmosphere of this song.
                </FormDescription>
              </FormItem>
            </div>

            {/* Optional Comments */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Additional Comments (Optional)</h3>

              <FormField
                control={form.control}
                name="aestheticComment"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Aesthetic Quality Comment</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Why did you rate the aesthetic quality this way?"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Optional explanation for your aesthetic quality rating.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="songDescription"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Song Description</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Describe the song in 1-4 sentences (genre, instruments, mood, etc.)"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Optional description covering genre, instruments, mood, musical attributes, or tempo.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end pt-6">
              <Button type="submit" disabled={isLoading} className="min-w-32">
                {isLoading ? 'Submitting...' : 'Submit Annotation'}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}